import produce from 'immer';

import { NextSafeVersionResponse, VulnsNextSafeVersionList } from '../../../shared/vulns/types';
import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';
import type { VulnsNextSafeVersionResponse } from '../../../typings/api';
import type { ReduxAction, ReduxActionResponse} from '../../types';

import { actionTypes } from './actions';
import type { VulnsStore } from './types';

const initialState: Readonly<VulnsStore> = Object.freeze({
  /**
   * AsyncStatus, tracks the network status as well as any error messages.
   */
  asyncStatus: {
    nextSafeVersion: getInitialStatus(),
    nextSafeVersionList: getInitialStatus(),
    createOrgSecurityReport: getInitialStatus(),
    remediationGuidance: getInitialStatus(),
  },
  /**
   * Remediation Guidance for Issues
   */
  remediationGuidance: {},
  /**
   * Deprecated - Next Safe Version of a given revision
   */
  nextSafeVersion: '',

  /**
   * Deprecated - List of next safe versions indexed by package locator and then revision
   */
  nextSafeVersionList: {},
});

const getRemediationGuidance = {
  [`${actionTypes.GET_REMEDIATION_GUIDANCE}_PENDING`]: (state: VulnsStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.remediationGuidance = getPendingStatus();
    }),
  [`${actionTypes.GET_REMEDIATION_GUIDANCE}_FULFILLED`]: (state: VulnsStore, action: ReduxActionResponse<NextSafeVersionResponse>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.remediationGuidance = getInitialStatus();
      if (action.payload.data) {
        draftState.remediationGuidance = action.payload.data;
      }
    }),
  [`${actionTypes.GET_REMEDIATION_GUIDANCE}_REJECTED`]: (state: VulnsStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.remediationGuidance = getErrorStatus(action.payload);
    }),
};

const getNextSafeVersion = {
  [`${actionTypes.GET_NEXT_SAFE_VERSION}_PENDING`]: (state: VulnsStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.nextSafeVersion = getPendingStatus();
    }),
  [`${actionTypes.GET_NEXT_SAFE_VERSION}_FULFILLED`]: (state: VulnsStore, action: ReduxActionResponse<VulnsNextSafeVersionResponse>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.nextSafeVersion = getInitialStatus();
      if (action.payload.data?.nextSafeVersion) {
        draftState.nextSafeVersion = action.payload.data?.nextSafeVersion;
      }
    }),
  [`${actionTypes.GET_NEXT_SAFE_VERSION}_REJECTED`]: (state: VulnsStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.nextSafeVersion = getErrorStatus(action.payload);
    }),
};

const getNextSafeVersionList = {
  [`${actionTypes.GET_NEXT_SAFE_VERSION_LIST}_PENDING`]: (state: VulnsStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.nextSafeVersionList = getPendingStatus();
    }),
  [`${actionTypes.GET_NEXT_SAFE_VERSION_LIST}_FULFILLED`]: (state: VulnsStore, action: ReduxActionResponse<VulnsNextSafeVersionList>) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.nextSafeVersionList = getInitialStatus();
      if (action.payload.data) {
        draftState.nextSafeVersionList = {
          ...state.nextSafeVersionList,
          ...action.payload.data,
        };
      }
    }),
  [`${actionTypes.GET_NEXT_SAFE_VERSION_LIST}_REJECTED`]: (state: VulnsStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.nextSafeVersion = getErrorStatus(action.payload);
    }),
};

const createOrgSecurityReport = {
  [`${actionTypes.CREATE_ORG_SECURITY_REPORT}_PENDING`]: (state: VulnsStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.createOrgSecurityReport = getPendingStatus();
    }),
  [`${actionTypes.CREATE_ORG_SECURITY_REPORT}_FULFILLED`]: (state: VulnsStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.createOrgSecurityReport = getInitialStatus();
    }),
  [`${actionTypes.CREATE_ORG_SECURITY_REPORT}_REJECTED`]: (state: VulnsStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.createOrgSecurityReport = getErrorStatus(action.payload);
    }),
};

export default createReducer<VulnsStore>(initialState, {
  ...getRemediationGuidance,
  ...getNextSafeVersion,
  ...getNextSafeVersionList,
  ...createOrgSecurityReport,
});
